import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useUpdateOrganization } from '@/shared/hooks/api/organizations'
import { useOrganizationDocuments } from '@/shared/hooks/use-organization-documents'
import { validateLatinCharacters } from '@/shared/lib/latinCharacterValidation'
import { getBusinessRegistrationLabelTranslationKey } from '@/features/organization-settings/lib/business-registration-label'

function doesStartWithPlaceOfRegistration(
  value: string,
  placeOfRegistration: string
) {
  const lower = value.toLowerCase()
  const prefix = placeOfRegistration.toLowerCase()
  return (
    lower.startsWith(prefix) ||
    value.startsWith(placeOfRegistration.toUpperCase())
  )
}

function getMaxLengthForBusinessRegistrationNumber(
  value: string,
  placeOfRegistration: string
) {
  if (doesStartWithPlaceOfRegistration(value, placeOfRegistration)) {
    return 25
  }
  return 23
}

type UpdateBusinessCertificateDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  organizationId: string
  placeOfRegistration: string | null
  /** Used for business registration label variant (legacy dialog). */
  countryForLabel: string
  initialBusinessRegistrationNumber: string
  initialCertificateFileName: string
}

export function UpdateBusinessCertificateDialog({
  open,
  onOpenChange,
  organizationId,
  placeOfRegistration,
  countryForLabel,
  initialBusinessRegistrationNumber,
  initialCertificateFileName,
}: UpdateBusinessCertificateDialogProps) {
  const { t } = useTranslation('settings')
  const { mutateAsync: updateOrganization, isPending } = useUpdateOrganization(
    organizationId
  )
  const { uploadDocument } = useOrganizationDocuments({ organizationId })

  const [state, setState] = useState({
    businessRegistrationNumber: initialBusinessRegistrationNumber,
    certificateFileName: initialCertificateFileName,
  })

  useEffect(() => {
    if (open) {
      setState({
        businessRegistrationNumber: initialBusinessRegistrationNumber,
        certificateFileName: initialCertificateFileName,
      })
    }
  }, [
    open,
    initialBusinessRegistrationNumber,
    initialCertificateFileName,
  ])

  const registrationPlace = placeOfRegistration ?? ''

  const { businessRegistrationNumber, certificateFileName } = state

  const latinError = validateLatinCharacters(
    businessRegistrationNumber,
    t('common:errors.latinCharacters', { ns: 'common' })
  )

  const maxLength = useMemo(() => {
    return getMaxLengthForBusinessRegistrationNumber(
      businessRegistrationNumber,
      registrationPlace
    )
  }, [businessRegistrationNumber, registrationPlace])

  const tooLongError = useMemo(() => {
    if (businessRegistrationNumber.length > maxLength) {
      return t(
        'organizationDetails.updateBusinessCertificateDialog.errors.lengthError',
        { maxLength }
      )
    }
    return undefined
  }, [businessRegistrationNumber, maxLength, t])

  const error = tooLongError || latinError || undefined

  const isSaveEnabled = !tooLongError && !latinError

  const onBusinessRegistrationNumberChange = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      const { id, value } = evt.target
      setState((prev) => ({ ...prev, [id]: value }))
    },
    []
  )

  const onFileChange = useCallback(
    async (evt: React.ChangeEvent<HTMLInputElement>) => {
      const file = evt.target.files?.[0]
      evt.target.value = ''
      if (!file) {
        return
      }
      const result = await uploadDocument(file)
      if (!result.success) {
        let msg = t('common:errors.uploadFile.generic', { ns: 'common' })
        if (result.errorMessage === 'INVALID_FILE_TYPE') {
          msg = t('common:errors.uploadFile.unsupportedFileType', {
            ns: 'common',
          })
        } else if (result.errorMessage === 'FILE_TOO_LARGE') {
          msg = t('common:errors.uploadFile.fileTooLarge', { ns: 'common' })
        }
        toast.error(msg)
        return
      }
      if (result.fileName) {
        setState((prev) => ({
          ...prev,
          certificateFileName: result.fileName ?? '',
        }))
      }
    },
    [uploadDocument, t]
  )

  const onSave = useCallback(async () => {
    try {
      await updateOrganization({
        businessRegistrationNumber,
        certificateFileName,
      })
      toast.success(
        t(
          'organizationDetails.updateBusinessCertificateDialog.notifications.updateSuccess'
        )
      )
      onOpenChange(false)
    } catch {
      toast.error(
        t(
          'organizationDetails.updateBusinessCertificateDialog.notifications.updateError'
        )
      )
    }
  }, [
    updateOrganization,
    businessRegistrationNumber,
    certificateFileName,
    onOpenChange,
    t,
  ])

  const businessIdLabelKey = getBusinessRegistrationLabelTranslationKey(countryForLabel)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl sm:max-w-xl" showCloseButton>
        <DialogHeader>
          <DialogTitle>
            {t('organizationDetails.updateBusinessCertificateDialog.title')}
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="businessRegistrationNumber">
              {t(businessIdLabelKey)}
            </Label>
            <Input
              id="businessRegistrationNumber"
              value={businessRegistrationNumber}
              onChange={onBusinessRegistrationNumberChange}
              maxLength={maxLength}
              aria-invalid={!!error}
            />
            <p className="text-sm text-muted-foreground">
              {t(
                'organizationDetails.updateBusinessCertificateDialog.fields.businessRegistrationNumber.hint'
              )}
            </p>
            {error ? (
              <p className="text-sm text-destructive">{error}</p>
            ) : null}
          </div>
          <div className="grid gap-2">
            <Label>
              {t(
                'organizationDetails.updateBusinessCertificateDialog.fields.businessCertificate.label'
              )}
            </Label>
            <p className="text-sm text-muted-foreground italic">
              {t(
                'organizationDetails.updateBusinessCertificateDialog.fields.businessCertificate.hint'
              )}
            </p>
            <Alert>
              <AlertDescription className="space-y-1">
                <p>
                  {t(
                    'organizationDetails.updateBusinessCertificateDialog.fields.businessCertificate.instruction1'
                  )}
                </p>
                <p>
                  {t(
                    'organizationDetails.updateBusinessCertificateDialog.fields.businessCertificate.instruction2'
                  )}
                </p>
              </AlertDescription>
            </Alert>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <Button type="button" variant="outline" asChild>
                <label className="cursor-pointer">
                  {t(
                    'organizationDetails.updateBusinessCertificateDialog.fields.businessCertificate.uploadButton'
                  )}
                  <input
                    type="file"
                    className="sr-only"
                    accept=".pdf,.png,.jpg,.jpeg,image/png,image/jpeg,application/pdf"
                    onChange={onFileChange}
                  />
                </label>
              </Button>
              {certificateFileName ? (
                <span className="truncate text-sm text-muted-foreground">
                  {certificateFileName}
                </span>
              ) : null}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('common:actions.cancel', { ns: 'common' })}
          </Button>
          <Button
            type="button"
            onClick={onSave}
            disabled={!isSaveEnabled || isPending}
          >
            {t('common:actions.save', { ns: 'common' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
